<template>
  <div>
    <v-row>
      <v-col cols="12" md="10" offset-md="1">
        <v-row>
          <v-col class="py-0">
            <v-form v-model="mainFormValid">
              <v-text-field
                v-model="internalValue.name"
                :label="$t('multiproject.fields.draw.name')"
                :rules="$options.rules.name"
                @input="change"
              />
            </v-form>
          </v-col>
          <v-col cols="auto" class="py-0">
            <v-select
              v-model="internalValue.type"
              :disabled="!create && !!internalValue.type"
              :items="typeItems"
              :label="$t('multiproject.fields.draw.type')"
              :rules="$options.rules.type"
              style="width: 200px"
              @change="change"
            />
          </v-col>
          <v-col cols="auto" class="py-0">
            <v-checkbox
              v-model="internalValue.defaultDraw"
              :disabled="!create && disabledDefaultOptions"
              :label="$t('multiproject.fields.draw.defaultDraw')"
              @change="change"
            />
          </v-col>
          <v-col v-if="internalValue.type === 'standard'" cols="12" class="py-0">
            <v-autocomplete
              v-model="internalValue.relatedDraw"
              :items="ccpmDrawItems"
              :label="$t('multiproject.fields.draw.relatedDraw')"
              @change="change"
            />
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="internalValue.roundList"
          :loading="dataTableLoading"
          :loading-text="$t('general.loading')"
          class="mb-5"
          disable-pagination
          hide-default-footer
          dense
        >
          <template v-slot:[`item.orderNumber`]="{ item }">
            <div class="font-weight-bold" v-text="item.orderNumber" />
          </template>
          <template v-slot:[`item.blue`]="{ item }">
            <ag-data-table-edit-dialog
              v-model="item.blue"
              :rules="$options.rules.colors"
              @input="change"
            />
          </template>
          <template v-slot:[`item.green`]="{ item }">
            <ag-data-table-edit-dialog
              v-model="item.green"
              :rules="$options.rules.colors"
              @input="change"
            />
          </template>
          <template v-slot:[`item.yellow`]="{ item }">
            <ag-data-table-edit-dialog
              v-model="item.yellow"
              :rules="$options.rules.colors"
              @input="change"
            />
          </template>
          <template v-slot:[`item.red`]="{ item }">
            <ag-data-table-edit-dialog
              v-model="item.red"
              :rules="$options.rules.colors"
              @input="change"
            />
          </template>
          <template v-slot:[`item.variability`]="{ item }">
            <ag-data-table-edit-dialog
              v-model="item.variability"
              :rules="$options.rules.variability"
              @input="change"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Helpers from 'src/utils/helpers'
import FormRules from 'src/utils/form-rules'

import AgDataTableEditDialog from 'src/components/common/AgDataTableEditDialog'

export default {
  name: 'DrawForm',
  components: {
    AgDataTableEditDialog,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        name: '',
        type: '',
        defaultDraw: false,
        relatedDraw: '',
        roundList: [],
      }),
    },
    drawList: {
      type: Array,
      required: true,
    },
    create: {
      type: Boolean,
      default: false,
    },
    dataTableLoading: {
      type: Boolean,
      default: true,
    },
    disabledDefaultOptions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    internalValue: null,
    mainFormValid: false,
  }),
  computed: {
    headers () {
      return [
        {
          text: this.$t('multiproject.fields.draw.round'),
          value: 'orderNumber',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.blue'),
          value: 'blue',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.green'),
          value: 'green',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.yellow'),
          value: 'yellow',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.red'),
          value: 'red',
          align: 'center',
          sortable: false,
        },
        {
          text: this.$t('multiproject.fields.draw.variability'),
          value: 'variability',
          align: 'center',
          sortable: false,
        },
      ]
    },
    isValidDraw () {
      return this.mainFormValid
        && this.internalValue.roundList.length === 100
        && this.internalValue.roundList.every(this.isValidRound)
        && this.internalValue.type
    },
    typeItems () {
      return [
        { value: 'standard', text: this.$t('multiproject.fields.draw.typeList.standard') },
        { value: 'ccpm', text: this.$t('multiproject.fields.draw.typeList.ccpm') },
      ]
    },
    ccpmDrawItems () {
      const ccpmDrawItems = this.drawList
        .filter(draw => draw.type === 'ccpm')
        .map(draw => ({ value: draw.id, text: draw.name }))

      return [
        { value: '', text: this.$t('general.none') },
        ...ccpmDrawItems,
      ]
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (value) {
        this.internalValue = Helpers.clone(value)
      },
    },
    isValidDraw: {
      immediate: true,
      handler () {
        this.$emit('validate', this.isValidDraw)
      },
    },
  },
  methods: {
    change () {
      this.$emit('input', this.internalValue)
      this.$emit('validate', this.isValidDraw)
    },
    isValidRound ({
      orderNumber, blue, green, yellow, red, variability,
    }) {
      return Helpers.isBetween(orderNumber, { min: 1, max: 100 })
        && Helpers.isBetween(blue, { min: 1, max: 9 })
        && Helpers.isBetween(green, { min: 1, max: 9 })
        && Helpers.isBetween(yellow, { min: 1, max: 9 })
        && Helpers.isBetween(red, { min: 1, max: 9 })
        && Helpers.isBetween(variability, { min: 0, max: 4 })
    },
  },
  rules: {
    colors: [
      FormRules.between('multiproject.fields.error.between', { min: 1, max: 9 }),
    ],
    variability: [
      FormRules.between('multiproject.fields.error.between', { min: 0, max: 4 }),
    ],
    name: [
      FormRules.required('error.form.required'),
      FormRules.minLength('error.form.minLength', { length: 10 }),
    ],
    type: [
      FormRules.required('error.form.required'),
    ],
  },
}
</script>
